import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { candidateService } from '@/services/api';

// Types
export interface SlotAvailabilityResponse {
  startTime: string;
  endTime: string;
  bookedInterviews: number;
  availableInterviews: number;
  maxInterviews: number;
}

export interface TimeSlot {
  uid: string;
  date: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  bookedInterviews: number;
  availableInterviews: number;
  maxInterviews: number;
}

interface BookedSlot {
  uid: string;
  interviewUid: string;
  date: string;
  startTime: string;
  endTime: string;
  status: 'confirmed' | 'cancelled' | 'Scheduled';
  candidateEmail?: string;
  candidateName?: string;
  jobTitle?: string;
  interviewType?: string[];
  durationInMinutes?: string;
}

interface CandidateProfile {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  resumeUrl?: string;
}

interface DeviceTest {
  camera: boolean;
  microphone: boolean;
  speaker: boolean;
}

interface CandidateState {
  profile: CandidateProfile | null;
  availableSlots: TimeSlot[];
  selectedDate: string | null;
  selectedSlot: TimeSlot | null;
  bookedSlot: BookedSlot | null;
  deviceTest: DeviceTest;
  interviewUid: string | null;
  slotUid: string | null;
  instantInterviewId: string | null;
  isTabSwitched: boolean;
  tabSwitchCount: number;
  isLoading: boolean;
  isSlotsLoading: boolean;
  isBookingLoading: boolean;
  error: string | null;
  slotsError: string | null;
  bookingError: string | null;
}

// Initial state
const initialState: CandidateState = {
  profile: null,
  availableSlots: [],
  selectedDate: null,
  selectedSlot: null,
  bookedSlot: JSON.parse(sessionStorage.getItem('bookedSlot') || 'null'),
  deviceTest: {
    camera: false,
    microphone: false,
    speaker: false,
  },
  interviewUid: sessionStorage.getItem('interviewUid'),
  slotUid: sessionStorage.getItem('slotUid'),
  instantInterviewId: sessionStorage.getItem('instantInterviewId'),
  isTabSwitched: false,
  tabSwitchCount: Number(sessionStorage.getItem('tabSwitchCount')) || 0,
  isLoading: false,
  isSlotsLoading: false,
  isBookingLoading: false,
  error: null,
  slotsError: null,
  bookingError: null,
};

// Async thunks
export const fetchAvailableSlots = createAsyncThunk(
  'candidate/fetchAvailableSlots',
  async (date: string, { rejectWithValue }) => {
    try {
      const response = await candidateService.getAvailableSlots(date);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch available slots';
      return rejectWithValue(message);
    }
  }
);

export const bookSlot = createAsyncThunk(
  'candidate/bookSlot',
  async (
    params: {
      candidateUId: string;
      slotBooking: {
        startTime: string;
        endTime: string;
        interviewDate: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await candidateService.bookSlot(params);

      if (response.data) {
        sessionStorage.setItem('slotUid', response.data.uId);

        // Return mapped data for the reducer
        return {
          uid: response.data.uId,
          interviewUid: response.data.uId, // Assuming uId is the interview/slot ID
          date: response.data.slotBooking.interviewDate,
          startTime: response.data.slotBooking.startTime,
          endTime: response.data.slotBooking.endTime,
          status: 'Scheduled',
          candidateEmail: response.data.candidate.email,
          candidateName: `${response.data.candidate.firstName} ${response.data.candidate.lastName}`,
          jobTitle: response.data.candidate.jobTitle,
          interviewType: response.data.slotBooking.interviewType,
          durationInMinutes: response.data.slotBooking.durationInMinutes,
        } as BookedSlot;
      }
      return rejectWithValue(
        response.error || response.message || 'Failed to book slot'
      );
    } catch (error: unknown) {
      return rejectWithValue('Failed to book slot');
    }
  }
);

// Slice
const candidateSlice = createSlice({
  name: 'candidate',
  initialState,
  reducers: {
    setSelectedDate: (state, action: PayloadAction<string | null>) => {
      state.selectedDate = action.payload;
      state.selectedSlot = null;
    },
    setSelectedSlot: (state, action: PayloadAction<TimeSlot | null>) => {
      state.selectedSlot = action.payload;
    },
    setIsTabSwitched: (state, action: PayloadAction<boolean>) => {
      state.isTabSwitched = action.payload;
    },
    incrementTabSwitchCount: state => {
      state.tabSwitchCount += 1;
      sessionStorage.setItem('tabSwitchCount', state.tabSwitchCount.toString());
    },
    clearBookedSlot: state => {
      state.bookedSlot = null;
      sessionStorage.removeItem('bookedSlot');
    },
  },
  extraReducers: builder => {
    // Fetch Available Slots
    builder
      .addCase(fetchAvailableSlots.pending, state => {
        state.isSlotsLoading = true;
        state.slotsError = null;
      })
      .addCase(fetchAvailableSlots.fulfilled, (state, action) => {
        state.isSlotsLoading = false;
        state.availableSlots = action.payload;
        state.slotsError = null;
      })
      .addCase(fetchAvailableSlots.rejected, (state, action) => {
        state.isSlotsLoading = false;
        state.slotsError = action.payload as string;
      });

    // Book Slot
    builder
      .addCase(bookSlot.pending, state => {
        state.isBookingLoading = true;
        state.bookingError = null;
      })
      .addCase(bookSlot.fulfilled, (state, action) => {
        state.isBookingLoading = false;
        state.bookedSlot = action.payload;
        state.slotUid = action.payload.uid;
        state.bookingError = null;
        sessionStorage.setItem('bookedSlot', JSON.stringify(action.payload));
        sessionStorage.removeItem('candidateUid');
      })
      .addCase(bookSlot.rejected, (state, action) => {
        state.isBookingLoading = false;
        state.selectedSlot = null;
        state.bookingError = action.payload as string;
      });
  },
});

export const {
  setSelectedDate,
  setSelectedSlot,
  setIsTabSwitched,
  incrementTabSwitchCount,
  clearBookedSlot,
} = candidateSlice.actions;

export default candidateSlice.reducer;
